"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { offlineContext, OFFLINE_ACCOUNT_EVENT } from "@/lib/offline/context";
import { COMPANION_SHARED_PLANS_CHANGED } from "@/lib/companion/events";
import { readCompanionResponse } from "@/lib/companion/http";
import { sameSharedMap, SHARED_MAP_MAX_BYTES, SHARED_MAP_TTL, validSharedMapRead, validSharedMapReference, type SharedMapRead, type SharedMapReference } from "@/lib/companion/shared-outing-map";

type SharedMapContext = {
  reference: SharedMapReference | null; data: SharedMapRead | null; displayed: boolean; error: string | null;
  select: (ref: SharedMapReference) => void; show: () => void; clear: () => void; retry: () => void;
};
const Context = createContext<SharedMapContext | null>(null);
export const useSharedOutingMap = () => useContext(Context);

/** Memory only. No root auth request until someone opens a specific shared map. */
export function SharedOutingMapProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const active = pathname === "/companion/explore" || pathname === "/companion/track";
  const [reference, setReference] = useState<SharedMapReference | null>(null);
  const [data, setData] = useState<(SharedMapRead & { expiresAt: number }) | null>(null);
  const accountGeneration = useRef(0);
  const [blocked, setBlocked] = useState(false);
  const [approved, setApproved] = useState(false), [error, setError] = useState<string | null>(null), [attempt, setAttempt] = useState(0);
  const select = useCallback((ref: SharedMapReference) => {
    if (!validSharedMapReference(ref)) return;
    setBlocked(false);
    setReference(current => current && sameSharedMap(current, ref) ? current : ref);
  }, []);
  const clear = useCallback(() => { accountGeneration.current++; setBlocked(false); setReference(null); setData(null); setApproved(false); setError(null); }, []);
  useEffect(() => { setData(null); setApproved(false); setError(null); }, [reference]);
  useEffect(() => {
    const changed = () => { accountGeneration.current++; setData(null); setApproved(false); setBlocked(true); setError("Your account changed. Check access again before reviewing this route."); };
    window.addEventListener(OFFLINE_ACCOUNT_EVENT, changed);
    return () => window.removeEventListener(OFFLINE_ACCOUNT_EVENT, changed);
  }, []);
  useEffect(() => {
    if (!reference || !active || blocked) { setData(null); return; }
    let alive = true, sequence = 0, controller: AbortController | null = null;
    let poll: ReturnType<typeof setTimeout>, deadline: ReturnType<typeof setTimeout>, expiry: ReturnType<typeof setTimeout>;
    const cancel = () => { sequence++; controller?.abort(); clearTimeout(poll); clearTimeout(deadline); };
    const hide = (message: string | null) => { clearTimeout(expiry); setData(null); setApproved(false); setError(message); };
    const check = async () => {
      cancel();
      if (document.hidden || !navigator.onLine) { hide("Reconnect with this page visible to review shared route access."); return; }
      const run = sequence, accountVersion = accountGeneration.current, started = Date.now();
      const request = new AbortController(); controller = request;
      deadline = setTimeout(() => { if (!alive || sequence !== run) return; cancel(); hide("Shared route access could not be checked. Review it again."); }, 12_000);
      try {
        const account = await offlineContext();
        if (!alive || run !== sequence || accountVersion !== accountGeneration.current) return;
        if (account.owner === "device") throw new Error("Sign in to review this shared route.");
        const q = new URLSearchParams({ id: reference.id, revision: String(reference.revision), snapshot: reference.snapshotId });
        const response = await fetch(`/api/companion/outing-map?${q}`, { cache: "no-store", signal: request.signal });
        const body = await readCompanionResponse(response, SHARED_MAP_MAX_BYTES);
        if (!alive || run !== sequence || accountVersion !== accountGeneration.current) return;
        if (!response.ok || !validSharedMapRead(body) || body.owner !== account.owner || !sameSharedMap(body, reference)) throw new Error("The route or your access changed. Reopen the current invitation to review it.");
        if (document.hidden || !navigator.onLine || Date.now() >= started + SHARED_MAP_TTL) throw new Error("Review shared route access again.");
        setData({ ...body, expiresAt: started + SHARED_MAP_TTL }); setError(null);
        clearTimeout(expiry);
        expiry = setTimeout(() => hide("Shared route access needs checking again."), Math.max(0, started + SHARED_MAP_TTL - Date.now()));
        poll = setTimeout(() => void check(), Math.max(0, started + 25_000 - Date.now()));
      } catch (problem) {
        if (alive && sequence === run && accountVersion === accountGeneration.current) hide(problem instanceof Error ? problem.message : "Shared route access could not be checked.");
      } finally { if (sequence === run) clearTimeout(deadline); }
    };
    // A hung refresh must not extend the previous response's permission lifetime.
    const refresh = () => { hide(null); void check(); };
    const visibility = () => { if (document.hidden || !navigator.onLine) { cancel(); hide("Shared route hidden until access is checked again."); } else refresh(); };
    const offline = () => { cancel(); hide("Shared route hidden until you reconnect and check access again."); };
    void check();
    document.addEventListener("visibilitychange", visibility); window.addEventListener("offline", offline); window.addEventListener("online", visibility);
    window.addEventListener(COMPANION_SHARED_PLANS_CHANGED, refresh);
    return () => { alive = false; cancel(); clearTimeout(expiry); document.removeEventListener("visibilitychange", visibility); window.removeEventListener("offline", offline); window.removeEventListener("online", visibility); window.removeEventListener(COMPANION_SHARED_PLANS_CHANGED, refresh); };
  }, [reference, active, attempt, blocked]);
  const current = active && !blocked && data && reference && sameSharedMap(data, reference) ? data : null;
  const value = useMemo(() => ({ reference, data: current, displayed: !!current && approved, error, select, clear,
    show: () => { if (current && current.expiresAt > Date.now() && !document.hidden && navigator.onLine) setApproved(true); },
    retry: () => { setBlocked(false); setData(null); setApproved(false); setError(null); setAttempt(a => a + 1); },
  }), [reference, current, approved, error, select, clear]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
